import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, ExternalLink, CheckCircle2, Wallet, MapPin, LifeBuoy,
  CalendarCheck, TrendingUp, Clock, Check, ShieldCheck, CreditCard,
  Download, ChevronLeft, ChevronRight,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/plans/$id")({
  head: () => ({
    meta: [
      { title: "Subscription Detail — Spayzo Admin" },
      { name: "description", content: "Detailed view of a merchant subscription." },
    ],
  }),
  component: Page,
});

type InvStatus = "Paid" | "Late" | "Refunded" | "Pending";
const INVOICES: { id: string; period: string; amount: React.ReactNode; status: InvStatus; date: string }[] = [
  { id: "#INV-0847", period: "Jun 2025", amount: <Money value="299" />, status: "Paid", date: "Jun 15, 2025" },
  { id: "#INV-0791", period: "May 2025", amount: <Money value="299" />, status: "Paid", date: "May 15, 2025" },
  { id: "#INV-0734", period: "Apr 2025", amount: <Money value="299" />, status: "Paid", date: "Apr 15, 2025" },
  { id: "#INV-0682", period: "Mar 2025", amount: <Money value="299" />, status: "Late", date: "Mar 17, 2025" },
];

const STATUS_STYLES: Record<InvStatus, string> = {
  Paid: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  Late: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  Refunded: "bg-sky-500/10 text-sky-700 border-sky-500/20",
  Pending: "bg-muted text-muted-foreground border-border/60",
};

const TONE: Record<string, string> = {
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  amber: "bg-amber-500",
  violet: "bg-primary",
};

function Page() {
  const { t } = useTranslation();
  const { id } = Route.useParams();

  const OVERVIEW = [
    { label: t("adminPlanDetail.overview.totalPaid"), value: <Money value="3,887" />, sub: t("adminPlanDetail.overview.totalPaidSub"), icon: Wallet },
    { label: t("adminPlanDetail.overview.activeSpaces"), value: "8 / 15", sub: t("adminPlanDetail.overview.activeSpacesSub"), icon: MapPin },
    { label: t("adminPlanDetail.overview.supportTickets"), value: "3", sub: t("adminPlanDetail.overview.supportTicketsSub"), icon: LifeBuoy },
    { label: t("adminPlanDetail.overview.bookingsMtd"), value: "142", sub: t("adminPlanDetail.overview.bookingsMtdSub"), icon: CalendarCheck },
    { label: t("adminPlanDetail.overview.revenueMtd"), value: <Money value="18,430" />, sub: t("adminPlanDetail.overview.revenueMtdSub"), icon: TrendingUp },
    { label: t("adminPlanDetail.overview.subscriptionAge"), value: t("adminPlanDetail.overview.subscriptionAgeValue"), sub: t("adminPlanDetail.overview.subscriptionAgeSub"), icon: Clock },
  ];

  const FEATURES = [
    { label: t("adminPlanDetail.features.spaces"), value: t("adminPlanDetail.features.spacesValue") },
    { label: t("adminPlanDetail.features.products"), value: t("adminPlanDetail.features.unlimited") },
    { label: t("adminPlanDetail.features.analytics"), value: t("adminPlanDetail.features.included") },
    { label: t("adminPlanDetail.features.support"), value: t("adminPlanDetail.features.included") },
    { label: t("adminPlanDetail.features.branding"), value: t("adminPlanDetail.features.included") },
    { label: t("adminPlanDetail.features.commission"), value: t("adminPlanDetail.features.commissionValue") },
  ];

  const STATUS_LABELS: Record<InvStatus, string> = {
    Paid: t("adminPlanDetail.invoiceStatus.paid"),
    Late: t("adminPlanDetail.invoiceStatus.late"),
    Refunded: t("adminPlanDetail.invoiceStatus.refunded"),
    Pending: t("adminPlanDetail.invoiceStatus.pending"),
  };

  const TIMELINE = [
    {
      title: t("adminPlanDetail.timeline.renewedTitle"),
      date: "Jun 15, 2025",
      detail: t("adminPlanDetail.timeline.renewedDetail"),
      tone: "emerald",
    },
    {
      title: t("adminPlanDetail.timeline.upgradedTitle"),
      date: "Apr 2, 2025",
      detail: t("adminPlanDetail.timeline.upgradedDetail"),
      tone: "sky",
    },
    {
      title: t("adminPlanDetail.timeline.lateTitle"),
      date: "Mar 15, 2025",
      detail: t("adminPlanDetail.timeline.lateDetail"),
      tone: "amber",
    },
    {
      title: t("adminPlanDetail.timeline.createdTitle"),
      date: "Mar 1, 2024",
      detail: t("adminPlanDetail.timeline.createdDetail"),
      tone: "violet",
    },
  ] as const;

  return (
    <AdminShell
      title={t("adminPlanDetail.title")}
      subtitle={t("adminPlanDetail.subtitle", { id: id.toUpperCase() })}
      actions={
        <>
          <Button asChild variant="outline" className="h-10 rounded-xl">
            <Link to="/admin/plans">
              <ArrowLeft className="h-4 w-4" /> {t("adminPlanDetail.back")}
            </Link>
          </Button>
          <Button className="h-10 rounded-xl" style={{ backgroundColor: "#5958F3" }}>
            <ExternalLink className="h-4 w-4" /> {t("adminPlanDetail.openMerchantPanel")}
          </Button>
        </>
      }
    >
      {/* Plan header */}
      <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <p className="font-display text-xl font-bold">{t("adminPlanDetail.proPlanName")}</p>
              <p className="text-sm text-muted-foreground">
                <span className="font-display text-base font-bold text-foreground"><Riyal /> 299</span> / {t("adminPlans.perMonth")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700">
              <CheckCircle2 className="me-1 h-3 w-3" /> {t("adminPlans.status.active")}
            </Badge>
            <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {t("adminPlanDetail.billedMonthly")}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Main grid */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* LEFT: main col */}
        <div className="space-y-6 lg:col-span-2">
          {/* Overview KPIs */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("adminPlanDetail.subscriptionOverview")}</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {OVERVIEW.map((k) => (
                <div key={k.label} className="rounded-xl border border-border/60 bg-surface p-4">
                  <div className="flex items-center justify-between">
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                      <k.icon className="h-4 w-4" />
                    </span>
                  </div>
                  <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{k.label}</p>
                  <p className="mt-1 font-display text-xl font-bold tracking-tight">{k.value}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{k.sub}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Plan features */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("adminPlanDetail.planFeaturesLimits")}</p>
            <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {FEATURES.map((f) => (
                <li key={f.label} className="flex items-start gap-3 rounded-xl border border-border/60 bg-surface p-3">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <Check className="h-3 w-3" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{f.label}</p>
                    <p className="text-xs text-muted-foreground">{f.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          {/* Billing history */}
          <Card className="overflow-hidden rounded-2xl border-border/60 shadow-soft">
            <div className="flex items-center justify-between border-b border-border/60 p-5">
              <div>
                <p className="font-display text-lg font-bold">{t("adminPlanDetail.recentBillingHistory")}</p>
                <p className="text-xs text-muted-foreground">{t("adminPlanDetail.last4Invoices")}</p>
              </div>
              <Button variant="outline" className="h-9 rounded-xl">
                <Download className="h-4 w-4" /> {t("adminPlanDetail.downloadInvoices")}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3 text-start font-semibold">{t("adminPlanDetail.table.invoice")}</th>
                    <th className="px-3 py-3 text-start font-semibold">{t("adminPlanDetail.table.period")}</th>
                    <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.amount")}</th>
                    <th className="px-3 py-3 text-start font-semibold">{t("adminPlans.table.status")}</th>
                    <th className="px-3 py-3 text-start font-semibold">{t("adminPlanDetail.table.date")}</th>
                    <th className="px-5 py-3 text-end font-semibold">{t("adminPlanDetail.table.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {INVOICES.map((inv) => (
                    <tr key={inv.id} className="border-t border-border/60 hover:bg-surface/50">
                      <td className="px-5 py-3.5 font-semibold">{inv.id}</td>
                      <td className="px-3 py-3.5">{inv.period}</td>
                      <td className="px-3 py-3.5 font-semibold">{inv.amount}</td>
                      <td className="px-3 py-3.5">
                        <Badge variant="outline" className={cn("rounded-full text-[10px] font-semibold", STATUS_STYLES[inv.status])}>
                          {STATUS_LABELS[inv.status]}
                        </Badge>
                      </td>
                      <td className="px-3 py-3.5 text-muted-foreground">{inv.date}</td>
                      <td className="px-5 py-3.5 text-end">
                        <Button variant="ghost" size="sm" className="h-8 rounded-lg">
                          <Download className="h-3.5 w-3.5" /> PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-border/60 p-3">
              <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs">
                <ChevronLeft className="h-3.5 w-3.5" /> {t("adminPlanDetail.previous")}
              </Button>
              <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs">
                {t("adminPlanDetail.next")} <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        </div>

        {/* RIGHT: sidebar */}
        <div className="space-y-6">
          {/* Merchant */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("adminPlanDetail.merchant")}</p>
            <div className="mt-4 flex items-center gap-3">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary/10 text-sm font-semibold text-primary">
                WH
              </div>
              <div className="min-w-0">
                <p className="truncate font-semibold">{t("adminPlanDetail.merchantName")}</p>
                <p className="truncate text-xs text-muted-foreground">{t("adminPlanDetail.merchantLocation")}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-500/10 text-[10px] font-semibold text-emerald-700">
                <CheckCircle2 className="me-1 h-3 w-3" /> {t("adminPlanDetail.verified")}
              </Badge>
              <span className="text-xs text-muted-foreground">{t("adminPlanDetail.sinceMar2024")}</span>
            </div>
          </Card>

          {/* Key dates */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("adminPlanDetail.keyDates")}</p>
            <dl className="mt-4 space-y-3 text-sm">
              {[
                [t("adminPlanDetail.startDate"), "Mar 15, 2024"],
                [t("adminPlanDetail.nextBilling"), "Jul 15, 2025"],
                [t("adminPlanDetail.renewal"), t("adminPlanDetail.autoRenewOn")],
                [t("adminPlanDetail.trialUsed"), t("adminPlanDetail.trialUsedValue")],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-border/60 pb-2 last:border-0 last:pb-0">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
                  <dd className="font-semibold">{v}</dd>
                </div>
              ))}
            </dl>
          </Card>

          {/* Payment method */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("adminPlanDetail.paymentMethod")}</p>
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-border/60 bg-surface p-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                <CreditCard className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold">Visa •••• 4291</p>
                  <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 text-[10px] font-semibold text-primary">
                    {t("adminPlanDetail.primary")}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{t("adminPlanDetail.expires")} 09/27</p>
              </div>
            </div>
          </Card>

          {/* Activity timeline */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("adminPlanDetail.activityTimeline")}</p>
            <ol className="mt-4 space-y-4">
              {TIMELINE.map((tl, i) => (
                <li key={i} className="relative ps-6">
                  <span className={cn("absolute start-0 top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-background", TONE[tl.tone])} />
                  {i < TIMELINE.length - 1 && (
                    <span className="absolute start-[5px] top-4 h-full w-px bg-border" />
                  )}
                  <p className="text-sm font-semibold">{tl.title}</p>
                  <p className="text-[11px] font-medium text-muted-foreground">{tl.date}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{tl.detail}</p>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
